/**
 * Define or edit one recurring item — FR-D1, ADR-0010 §1. A real kit `Dialog`
 * since Wave 15C (C14 §10.6 item 7) — it used to replace the whole calendar
 * screen as an inline card — laid out as the Branches dialog is.
 *
 * **The server is the validator.** Every keystroke that changes the shape of
 * the recurrence goes to `POST /items/check`, which runs the same
 * `domain.validate` the save runs and returns both the refusals and — the
 * point of the screen — *the next four periods this rule would produce*.
 *
 * That preview is not decoration. A firm defining a recurrence is asking one
 * question: **when will the first case appear, and what will it be called.**
 * Answering it before the save is the difference between a calendar they trust
 * and one they audit against a spreadsheet.
 *
 * The period-format options come from the same call, because which formats a
 * frequency may use is a rule — a monthly item labelled by financial year
 * gives twelve periods one name and eleven of them are never generated — and a
 * rule restated in TypeScript is a rule that drifts.
 */

import {
  Alert,
  Button,
  type ColumnDef,
  DataTable,
  Dialog,
  EmptyState,
  Input,
  Select,
  formatDate,
} from '@fiveox/ui'
import { useCallback, useEffect, useState } from 'react'

import { ApiError } from '../../lib/api'
import {
  type CalendarItem,
  type CheckResult,
  type DueRule,
  type Frequency,
  type ItemDraft,
  type PeriodFormat,
  type PeriodPlan,
  type Vocabulary,
  blankDraft,
  calendar,
  frequencyLabel,
  groupIssues,
  monthName,
  toDraft,
  toggleMonth,
  withDueRule,
  withFrequency,
} from '../../lib/api/calendar'
import type { Service } from '../../lib/api/services'
import { DialogActions, FORM_GRID, FormRow } from '../../components/page'

const DUE_RULE_LABELS: Record<DueRule, string> = {
  day_of_month: 'A day of the month',
  last_day: 'The last day of the month',
  days_after_period_end: 'A number of days after the period ends',
}

const FORMAT_HINTS: Record<PeriodFormat, string> = {
  'MM-YYYY': 'By month — 04-2026',
  'YYYY-YY': 'By financial year — 2026-27',
  'AY YYYY-YY': 'By assessment year — AY 2027-28',
}

const PREVIEW_COLUMNS: ColumnDef<PeriodPlan>[] = [
  {
    key: 'period',
    label: 'Period',
    width: 110,
    render: (p) => <span className="text-foreground font-medium">{p.period}</span>,
  },
  {
    key: 'covers',
    label: 'Covers',
    width: 200,
    render: (p) => (
      <span className="text-muted-foreground whitespace-nowrap">
        {formatDate(p.period_start)} → {formatDate(p.period_end)}
      </span>
    ),
  },
  {
    key: 'due',
    label: 'Due',
    width: 110,
    render: (p) => <span className="whitespace-nowrap">{formatDate(p.due_date)}</span>,
  },
  {
    key: 'opens',
    label: 'Case opens',
    width: 110,
    render: (p) => (
      <span className="text-muted-foreground whitespace-nowrap">{formatDate(p.opens_on)}</span>
    ),
  },
]

export function ItemDialog({
  item,
  services,
  vocabulary,
  onClose,
  onSaved,
}: {
  item: CalendarItem | null
  services: Service[]
  vocabulary: Vocabulary | null
  onClose: () => void
  onSaved: () => void
}) {
  // Only recurring services can carry an item: `is_recurring` is the catalog's
  // own declaration that the thing repeats (Wave 2B), and the server refuses
  // the rest. Offering them here and letting the save fail would be rude.
  const recurring = services.filter((service) => service.is_recurring)

  const [draft, setDraft] = useState<ItemDraft>(
    item ? toDraft(item) : blankDraft(recurring[0]?.id ?? ''),
  )
  const [check, setCheck] = useState<CheckResult | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)
  /** A save was pressed — so a missing required answer says so under its
   *  field, not only in a tooltip (C27, UAT report #13). */
  const [attempted, setAttempted] = useState(false)

  const formats: PeriodFormat[] =
    check?.period_formats ?? vocabulary?.period_formats_for[draft.frequency] ?? ['MM-YYYY']

  const revalidate = useCallback(async (next: ItemDraft) => {
    try {
      const body = await calendar.check({ ...next, service_id: next.service_id || undefined })
      setCheck(body.data)
    } catch {
      // A failed check leaves the last answer on screen rather than clearing
      // it: the save is still validated by the server, so the worst case here
      // is a stale preview, and blanking the panel on a dropped request reads
      // as the rule having become valid.
    }
  }, [])

  useEffect(() => {
    void revalidate(draft)
    // Re-checked on every change to the recurrence itself. Not on the name or
    // the description, which cannot change what is produced.
  }, [
    revalidate,
    draft.service_id,
    draft.frequency,
    draft.months.join(','),
    draft.period_format,
    draft.due_rule,
    draft.due_month_offset,
    draft.due_day,
    draft.due_offset_days,
    draft.lead_days,
    draft.tag,
  ])

  const set = (changes: Partial<ItemDraft>) => setDraft((current) => ({ ...current, ...changes }))

  const save = async () => {
    setError(null)
    if (!draft.service_id) {
      setAttempted(true)
      return
    }
    setSaving(true)
    try {
      if (item) await calendar.update(item.id, draft)
      else await calendar.create(draft)
      onSaved()
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'That item could not be saved.')
    } finally {
      setSaving(false)
    }
  }

  const errors = groupIssues(check?.errors ?? [])
  const warnings = groupIssues(check?.warnings ?? [])
  const problem = (field: string) => errors[field]?.[0]
  const caution = (field: string) => warnings[field]?.[0]
  // A missing service no longer disables the button (C27, UAT report #13):
  // pressing it says what is missing, under the field, where a tooltip on a
  // greyed-out button said it to nobody on a phone.
  const blocked = saving || check?.ok === false
  const why = check?.ok === false ? `${check.errors.length} to fix before this can be saved.` : undefined
  const serviceError =
    problem('service_id') ?? (attempted && !draft.service_id ? 'Choose a service.' : null)

  return (
    <Dialog
      open
      onClose={onClose}
      title={item ? `Edit “${item.name}”` : 'Add a recurring item'}
      error={error}
      width="xl"
    >
      <p className="text-muted-foreground mb-4 text-sm">
        ProDesk ships no compliance calendar of its own — the dates here are yours, and only what
        you define is generated.
      </p>

      {recurring.length === 0 && (
        <div className="mb-4">
          <Alert tone="warning">
            None of your services is marked as recurring, and a calendar item has to open a case on
            one that is. Turn on “recurring” on the service first.
          </Alert>
        </div>
      )}

      <div className={FORM_GRID}>
        <FormRow label="Name" htmlFor="item-name">
          <Input
            id="item-name"
            value={draft.name}
            placeholder="GSTR-3B"
            onChange={(e) => set({ name: e.target.value })}
          />
        </FormRow>

        <FormRow label="Service" htmlFor="item-service" required>
          <Select
            id="item-service"
            value={draft.service_id}
            onChange={(e) => set({ service_id: e.target.value, plan_id: null })}
            hint="Its workflow is the one a generated case runs."
            error={serviceError}
            required
            aria-required="true"
          >
            {/* A placeholder, not a choice: a calendar item must open a case
                on a service, so the empty option cannot be picked back. */}
            <option value="" disabled hidden>
              Choose a service…
            </option>
            {recurring.map((service) => (
              <option key={service.id} value={service.id}>
                {service.name}
              </option>
            ))}
          </Select>
        </FormRow>

        <FormRow label="How often" htmlFor="item-frequency">
          <Select
            id="item-frequency"
            value={draft.frequency}
            onChange={(e) => {
              const frequency = e.target.value as Frequency
              const allowed = vocabulary?.period_formats_for[frequency] ?? ['MM-YYYY']
              setDraft((current) => withFrequency(current, frequency, allowed))
            }}
          >
            {(vocabulary?.frequencies ?? []).map((frequency) => (
              <option key={frequency} value={frequency}>
                {frequencyLabel(frequency)}
              </option>
            ))}
          </Select>
        </FormRow>

        {draft.frequency === 'custom_months' && (
          <FormRow label="Which months">
            <div role="group" aria-label="Which months" className="flex flex-wrap gap-1.5 sm:pt-1">
              {Array.from({ length: 12 }, (_, index) => index + 1).map((month) => {
                const on = draft.months.includes(month)
                return (
                  <Button
                    key={month}
                    size="sm"
                    variant={on ? 'primary' : 'outline'}
                    aria-pressed={on}
                    onClick={() => set({ months: toggleMonth(draft.months, month) })}
                  >
                    {monthName(month).slice(0, 3)}
                  </Button>
                )
              })}
            </div>
          </FormRow>
        )}

        <FormRow label="Period name" htmlFor="item-format">
          <Select
            id="item-format"
            value={draft.period_format}
            onChange={(e) => set({ period_format: e.target.value as PeriodFormat })}
            error={problem('period_format') ?? null}
            hint="Each period must get its own name, or only the first one is ever generated."
          >
            {formats.map((format) => (
              <option key={format} value={format}>
                {FORMAT_HINTS[format]}
              </option>
            ))}
          </Select>
        </FormRow>

        <FormRow label="Due" htmlFor="item-due-rule">
          <div className="space-y-3">
            <Select
              id="item-due-rule"
              value={draft.due_rule}
              onChange={(e) => setDraft((c) => withDueRule(c, e.target.value as DueRule))}
              hint="Counted from the end of the period, never from today."
            >
              {(vocabulary?.due_rules ?? []).map((rule) => (
                <option key={rule} value={rule}>
                  {DUE_RULE_LABELS[rule]}
                </option>
              ))}
            </Select>
            {draft.due_rule === 'days_after_period_end' ? (
              <Input
                label="Days after the period ends"
                type="number"
                value={String(draft.due_offset_days ?? 0)}
                onChange={(e) => set({ due_offset_days: Number(e.target.value) })}
              />
            ) : (
              <div className="grid gap-3 sm:grid-cols-2">
                <Select
                  label="Months after the period ends"
                  value={String(draft.due_month_offset)}
                  onChange={(e) => set({ due_month_offset: Number(e.target.value) })}
                >
                  {[0, 1, 2, 3, 4, 5, 6, 9, 12].map((offset) => (
                    <option key={offset} value={offset}>
                      {offset === 0
                        ? 'The same month'
                        : offset === 1
                          ? 'The next month'
                          : `${offset} months`}
                    </option>
                  ))}
                </Select>
                {draft.due_rule === 'day_of_month' && (
                  <Input
                    label="Day"
                    type="number"
                    value={String(draft.due_day ?? 1)}
                    error={problem('due_day')}
                    onChange={(e) => set({ due_day: Number(e.target.value) })}
                  />
                )}
              </div>
            )}
          </div>
        </FormRow>

        <FormRow label="Open the case" htmlFor="item-lead">
          <Input
            id="item-lead"
            type="number"
            value={String(draft.lead_days)}
            error={caution('lead_days')}
            hint="This many days before it is due."
            onChange={(e) => set({ lead_days: Number(e.target.value) })}
          />
        </FormRow>

        <FormRow label="Only clients tagged" htmlFor="item-tag" optional>
          <Input
            id="item-tag"
            value={draft.tag ?? ''}
            placeholder="gst"
            hint="Left blank, every client the service applies to."
            onChange={(e) => set({ tag: e.target.value || null })}
          />
        </FormRow>

        <FormRow label="Generate from" htmlFor="item-from" optional>
          <Input
            id="item-from"
            type="date"
            value={draft.effective_from ?? ''}
            onChange={(e) => set({ effective_from: e.target.value || null })}
          />
        </FormRow>
      </div>

      <div className="mt-4 space-y-3">
        {caution('tag') && <Alert tone="warning">{caution('tag')}</Alert>}
        {caution('service_id') && <Alert tone="warning">{caution('service_id')}</Alert>}
        {errors[''] && <Alert tone="error">{errors[''][0]}</Alert>}
      </div>

      {/* The preview. Shown even when the rule is invalid: a firm looking at
          "these are the wrong dates" learns more from seeing them than from
          an empty box. */}
      <section aria-labelledby="item-preview" className="border-border mt-6 border-t pt-5">
        <h3 id="item-preview" className="text-foreground text-sm font-semibold">
          What this will produce
        </h3>
        <p className="text-muted-foreground mb-3 text-xs">
          The next few periods, worked out by the same rule the save uses.
        </p>
        {check?.next_periods?.length ? (
          <DataTable
            columns={PREVIEW_COLUMNS}
            rows={check.next_periods}
            rowKey={(period) => period.period}
            className="bg-card"
          />
        ) : (
          <EmptyState
            variant="outline"
            title="Nothing to show yet"
            description="Choose a frequency and a due date and the next few periods appear here."
          />
        )}
      </section>

      <DialogActions>
        {check?.ok === false && (
          <span className="text-destructive self-center text-xs sm:mr-auto">
            {check.errors.length} to fix
          </span>
        )}
        <Button variant="ghost" onClick={onClose}>
          Cancel
        </Button>
        <span title={why}>
          <Button onClick={() => void save()} disabled={blocked} loading={saving}>
            {item ? 'Save' : 'Add the item'}
          </Button>
        </span>
      </DialogActions>
    </Dialog>
  )
}
